/**
 * Contact Form DataLayer Tracking
 * Tracks form_view, form_start, form_interaction, form_error, and form_submit events
 */

(function() {
  'use strict';

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  const form = document.getElementById('contact_form');
  if (!form) return;

  // Map field names and positions
  const fieldMap = {
    'ContactFormSalutation': { name: 'Salutation', position: '1' },
    'ContactFormFirstName': { name: 'First Name', position: '2' },
    'ContactFormLastName': { name: 'Last Name', position: '3' },
    'ContactFormEmail': { name: 'Email', position: '4' },
    'ContactFormCountryDropdown': { name: 'Country', position: '5' },
    'ContactFormType': { name: 'Request Concern', position: '6' },
    'ContactFormDescription': { name: 'Message', position: '7' },
    'ContactFormAcceptsMarketing': { name: 'Marketing Consent', position: '8' }
  };

  // Track form_view event when form is displayed
  window.dataLayer.push({
    'event': 'form_view',
    'form_name': 'Contact Form'
  });

  // Track form_start event (first interaction)
  let formStarted = false;

  function trackFormStart(fieldId, fieldName, fieldPosition) {
    if (formStarted) return;
    formStarted = true;

    window.dataLayer.push({
      'event': 'form_start',
      'form_name': 'Contact Form',
      'form_field_name': fieldName,
      'form_field_number': fieldPosition
    });
  }

  // Track input fields (focus event)
  const inputs = form.querySelectorAll('input[type="text"], input[type="email"], textarea');
  inputs.forEach((input) => {
    input.addEventListener('focus', () => {
      const fieldInfo = fieldMap[input.id];
      if (fieldInfo) {
        trackFormStart(input.id, fieldInfo.name, fieldInfo.position);
      }
    }, { once: true });
  });

  // Track dropdown buttons (click event)
  const dropdownButtons = form.querySelectorAll('.dropdown__button');
  dropdownButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const dropdown = button.closest('.dropdown');
      const fieldId = dropdown.id || dropdown.querySelector('[data-dropdown-value]').id;
      const fieldInfo = fieldMap[fieldId];
      if (fieldInfo) {
        trackFormStart(fieldId, fieldInfo.name, fieldInfo.position);
      }
    }, { once: true });
  });

  // Track checkbox (change event)
  const checkbox = form.querySelector('#ContactFormAcceptsMarketing');
  if (checkbox) {
    checkbox.addEventListener('change', () => {
      const fieldInfo = fieldMap[checkbox.id];
      if (fieldInfo) {
        trackFormStart(checkbox.id, fieldInfo.name, fieldInfo.position);
      }
    }, { once: true });
  }

  // Track form_interaction event (field completion)
  function trackFormInteraction(fieldId, fieldName, fieldPosition) {
    window.dataLayer.push({
      'event': 'form_interaction',
      'form_name': 'Contact Form',
      'form_field_name': fieldName,
      'form_field_number': fieldPosition
    });
  }

  // Track input fields (blur event - when user leaves the field)
  inputs.forEach((input) => {
    input.addEventListener('blur', () => {
      if (input.value.trim() !== '') {
        const fieldInfo = fieldMap[input.id];
        if (fieldInfo) {
          trackFormInteraction(input.id, fieldInfo.name, fieldInfo.position);
        }
      }
    });
  });

  // Track dropdowns (change event on hidden input)
  const dropdownInputs = form.querySelectorAll('[data-dropdown-value]');
  dropdownInputs.forEach((input) => {
    input.addEventListener('change', () => {
      if (input.value) {
        const fieldInfo = fieldMap[input.id] || fieldMap[input.closest('.dropdown')?.id];
        if (fieldInfo) {
          trackFormInteraction(input.id || input.closest('.dropdown')?.id, fieldInfo.name, fieldInfo.position);
        }
      }
    });
  });

  // Track checkbox (change event)
  if (checkbox) {
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        const fieldInfo = fieldMap[checkbox.id];
        if (fieldInfo) {
          trackFormInteraction(checkbox.id, fieldInfo.name, fieldInfo.position);
        }
      }
    });
  }

  // Track form_error event (validation errors)
  function trackFormError(fieldName, errorMessage) { 
    window.dataLayer.push({
      'event': 'form_error',
      'form_name': 'Contact Form',
      'error_message': errorMessage,
      'error_field': fieldName
    });
  }

  // Map field names for error tracking
  const errorFieldMap = {
    'salutation': 'Salutation',
    'first_name': 'First Name',
    'last_name': 'Last Name',
    'email': 'Email',
    'country': 'Country',
    'type': 'Request Concern',
    'description': 'Message',
    'data_privacy': 'Data Privacy'
  };

  // Map error messages (internal naming)
  const errorMessageMap = {
    'empty_field': 'This field must not be empty',
    'invalid_email': 'Invalid email address',
    'option_check': 'This option must be checked'
  };

  // Helper function to process and track all validation errors
  function processValidationErrors(errors) {
    if (errors.length > 0) {
      // Track each error separately
      errors.forEach((error) => {
        const fieldName = errorFieldMap[error.element.name] || error.element.name;
        let errorMessage = error.message;
        
        // Map error message to internal naming
        if (errorMessage.includes('empty') || errorMessage.includes('required')) {
          errorMessage = errorMessageMap.empty_field;
        } else if (errorMessage.includes('email') || errorMessage.includes('invalid')) {
          errorMessage = errorMessageMap.invalid_email;
        } else if (errorMessage.includes('check')) {
          errorMessage = errorMessageMap.option_check;
        }
        
        trackFormError(fieldName, errorMessage);
      });
    }
  }

  // Track validation errors when form submission fails
  // Listen for jQuery Validate invalid-form event
  if (typeof $ !== 'undefined' && $.validator) {
    $(form).on('invalid-form.validate', function(event, validator) {
      const errors = validator.errorList;
      processValidationErrors(errors);
    });
  }

  // Fallback: Also listen for submit and check for errors
  form.addEventListener('submit', (e) => {
    // Wait for jQuery Validate to process
    setTimeout(() => {
      if (typeof $ !== 'undefined' && $.validator) {
        const validator = $(form).data('validator');
        if (validator && !validator.form()) {
          const errors = validator.errorList;
          processValidationErrors(errors);
        }
      }
    }, 200);
  });

  // Track form_submit event (successful submission)
  let formSubmitted = false;
  
  function trackFormSubmit(submissionId, requestConcern) {
    // Prevent multiple submissions
    if (formSubmitted) return;
    formSubmitted = true;

    window.dataLayer.push({
      'event': 'form_submit',
      'form_name': 'Contact Form',
      'form_submission_id': submissionId || '',
      'request_concerns': requestConcern || ''
    });
  }

  // Generate submission ID
  function generateSubmissionId() {
    return Date.now().toString() + Math.random().toString(36).substring(2, 9);
  }

  // Store form data before submission
  let formDataBeforeSubmit = null;

  // Capture form data when submit is attempted (before AJAX)
  form.addEventListener('submit', () => {
    // Reset flag on new submission attempt
    formSubmitted = false;
    const typeInput = form.querySelector('[name="type"]');
    formDataBeforeSubmit = {
      requestConcern: typeInput ? typeInput.value : ''
    };
  });

  // Listen for successful form submission
  // Method 1: Watch for existing 'contact_form_sent' event in dataLayer
  const originalPush = window.dataLayer.push;
  window.dataLayer.push = function(...args) {
    const result = originalPush.apply(this, args);
    
    // Check if it's the contact_form_sent event
    if (args[0] && args[0].event === 'contact_form_sent' && !formSubmitted) {
      const requestConcern = formDataBeforeSubmit?.requestConcern || '';
      const submissionId = generateSubmissionId();
      trackFormSubmit(submissionId, requestConcern);
    }
    
    return result;
  };

  // Method 2: Watch for class changes on success alert element (backup)
  const successAlert = form.querySelector('[data-alert="success"]');
  if (successAlert) {
    const alertObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('alert-success') && !target.classList.contains('d-none') && !formSubmitted) {
            const requestConcern = formDataBeforeSubmit?.requestConcern || '';
            const submissionId = generateSubmissionId();
            trackFormSubmit(submissionId, requestConcern);
            alertObserver.disconnect();
          }
        }
      });
    });

    alertObserver.observe(successAlert, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  // Method 3: Listen for custom event if form emits it (backup)
  document.addEventListener('contactFormSubmitted', (e) => {
    if (!formSubmitted) {
      const submissionId = e.detail?.submissionId || generateSubmissionId();
      const requestConcern = e.detail?.requestConcern || formDataBeforeSubmit?.requestConcern || '';
      trackFormSubmit(submissionId, requestConcern);
    }
  });

})();
